import React, { Component, createRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import '../css/PDFModal.css';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

class PDFPreviewer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      imageSrc: null,
      imageWidth: null,
      imageHeight: null,
      imageX: 100,
      imageY: 100,
      dragging: false,
      resizing: false,
      offsetX: 0,
      offsetY: 0,
      pageWidth: 0,
      pageHeight: 0,
      numPages: null,
      scale: 1,
      pendingImageFile: null,
      imagePlacedFromParams: false,
      currentPage: props.imagePlacementParams?.page || 1,
    };
    this.imageRef = createRef();
    this.pdfPageRef = createRef();
  }

  componentDidMount() {
    if (this.props.imageForSign) {
      this.setState({ pendingImageFile: this.props.imageForSign });
    }

    if (this.props.imagePlacementParams?.page) {
      this.setState({ currentPage: this.props.imagePlacementParams.page });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.imageForSign !== this.props.imageForSign &&
      !this.state.imagePlacedFromParams
    ) {
      const { pageWidth, pageHeight } = this.state;
      if (pageWidth && pageHeight) {
        this.loadImageFromFile(this.props.imageForSign);
        this.setState({ imagePlacedFromParams: true });
      } else {
        this.setState({ pendingImageFile: this.props.imageForSign });
      }
    }

    if (prevState.currentPage !== this.state.currentPage) {
      this.loadPageSize(this.state.currentPage);
    }
  }

  clamp = (val, min, max) => Math.max(min, Math.min(val, max));

  handleDragStart = (e) => {
    this.setState({
      dragging: true,
      offsetX: e.clientX - this.state.imageX,
      offsetY: e.clientY - this.state.imageY,
    });
    document.addEventListener('mousemove', this.handleDragging);
    document.addEventListener('mouseup', this.handleDragEnd);
  };

  handleDragging = (e) => {
    const { offsetX, offsetY, imageWidth, imageHeight, pageWidth, pageHeight } = this.state;
    let newX = this.clamp(e.clientX - offsetX, 0, pageWidth - imageWidth);
    let newY = this.clamp(e.clientY - offsetY, 0, pageHeight - imageHeight);
    this.setState({ imageX: newX, imageY: newY });
  };

  handleDragEnd = () => {
    this.setState({ dragging: false });
    document.removeEventListener('mousemove', this.handleDragging);
    document.removeEventListener('mouseup', this.handleDragEnd);
  };

  handleResizeStart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    this.setState({
      resizing: true,
      resizeStartWidth: this.state.imageWidth,
      resizeStartHeight: this.state.imageHeight,
      resizeStartX: e.clientX,
    });
    document.addEventListener('mousemove', this.handleResizing);
    document.addEventListener('mouseup', this.handleResizeEnd);
  };

  handleResizing = (e) => {
    const {
      imageX,
      imageY,
      resizeStartX,
      resizeStartWidth,
      resizeStartHeight,
      pageWidth,
      pageHeight,
    } = this.state;

    const deltaX = e.clientX - resizeStartX;
    const scale = (resizeStartWidth + deltaX) / resizeStartWidth;

    let newWidth = resizeStartWidth * scale;
    let newHeight = resizeStartHeight * scale;

    if (newWidth + imageX > pageWidth) {
      newWidth = pageWidth - imageX;
      newHeight = (resizeStartHeight / resizeStartWidth) * newWidth;
    }
    if (newHeight + imageY > pageHeight) {
      newHeight = pageHeight - imageY;
      newWidth = (resizeStartWidth / resizeStartHeight) * newHeight;
    }

    if (newWidth > 20 && newHeight > 20) {
      this.setState({ imageWidth: newWidth, imageHeight: newHeight });
    }
  };

  handleResizeEnd = () => {
    this.setState({ resizing: false });
    document.removeEventListener('mousemove', this.handleResizing);
    document.removeEventListener('mouseup', this.handleResizeEnd);
  };

  handleDocumentLoadSuccess = async (pdf) => {
    this.pdfDoc = pdf;
    this.setState({ numPages: pdf.numPages }, () => {
      const pageToLoad = this.state.currentPage || 1;
      this.loadPageSize(pageToLoad);
    });
  };

  loadImageFromFile = (file) => {
    if (!file || !this.pdfPageRef.current) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = this.pdfPageRef.current.querySelector('canvas');
        const actualCanvasHeight = canvas ? canvas.offsetHeight : this.state.pageHeight;
        const actualCanvasWidth = canvas ? canvas.offsetWidth : this.state.pageWidth;

        const maxWidth = actualCanvasWidth * 0.5;
        const maxHeight = actualCanvasHeight * 0.5;

        const widthRatio = maxWidth / img.naturalWidth;
        const heightRatio = maxHeight / img.naturalHeight;
        const scaleImage = Math.min(widthRatio, heightRatio, 1);

        const scaledWidth = img.naturalWidth * scaleImage;
        const scaledHeight = img.naturalHeight * scaleImage;

        const params = this.props.imagePlacementParams;
        const scaleFactor = this.state.scale;

        let imageX, imageY, imageWidth, imageHeight;

        if (params) {
          imageX = params.x * scaleFactor;
          imageY = actualCanvasHeight - (params.y * scaleFactor);
          imageWidth = params.width * scaleFactor;
          imageHeight = params.height * scaleFactor;
        } else {
          imageX = (actualCanvasWidth - scaledWidth) / 2;
          imageY = (actualCanvasHeight - scaledHeight) / 2;
          imageWidth = scaledWidth;
          imageHeight = scaledHeight;
        }

        this.setState({
          imageSrc: e.target.result,
          imageX,
          imageY,
          imageWidth,
          imageHeight,
        });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  generateResizedImage = () => {
    const imageFile = this.props.imageForSign;
    const {
      imageWidth,
      imageHeight,
      imageX,
      imageY,
      pageHeight,
      currentPage,
      scale,
    } = this.state;

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = imageWidth;
      canvas.height = imageHeight;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, imageWidth, imageHeight);

      canvas.toBlob((blob) => {
        const resizedFile = new File([blob], imageFile.name, {
          type: imageFile.type,
          lastModified: Date.now(),
        });

        this.props.onImageProcessed({
          file: resizedFile,
          coords: {
            page: currentPage,
            x: imageX / scale,
            y: (pageHeight - imageY) / scale,
            width: imageWidth / scale,
            height: imageHeight / scale,
          },
        });
      }, imageFile.type);
    };

    img.src = URL.createObjectURL(imageFile);
  };

  loadPageSize = async (pageNumber) => {
    const pdf = this.pdfDoc;
    if (!pdf) return;

    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: 1 });

    const containerPadding = 40;
    const maxWidth = window.innerWidth - containerPadding;
    const maxHeight = window.innerHeight - 150;

    const widthRatio = maxWidth / viewport.width;
    const heightRatio = maxHeight / viewport.height;
    const newScale = Math.min(widthRatio, heightRatio, 1);

    const newPageWidth = viewport.width * newScale;
    const newPageHeight = viewport.height * newScale;

    this.setState({
      scale: newScale,
      pageWidth: newPageWidth,
      pageHeight: newPageHeight,
    }, () => {
      const { imageSrc, imageWidth, imageHeight } = this.state;

      if (imageSrc && imageWidth && imageHeight) {
        const centerX = (newPageWidth - imageWidth) / 2;
        const centerY = (newPageHeight - imageHeight) / 2;
        this.setState({ imageX: centerX, imageY: centerY });
      }

      if (this.state.pendingImageFile && !this.state.imagePlacedFromParams) {
        requestAnimationFrame(() => {
          this.loadImageFromFile(this.state.pendingImageFile);
          this.setState({
            pendingImageFile: null,
            imagePlacedFromParams: true,
          });
        });
      }
    });
  };

  render() {
    const { base64Data, onClose } = this.props;
    const {
      imageSrc,
      imageX,
      imageY,
      imageWidth,
      imageHeight,
      currentPage,
      numPages,
      scale,
    } = this.state;

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div
          className="modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          <button className="close-button" onClick={onClose}>×</button>

          <div className="pdf-borders" style={{ position: 'relative' }} ref={this.pdfPageRef}>
            <Document
              file={base64Data}
              onLoadError={(err) => alert('Помилка PDF: ' + err.message)}
              onLoadSuccess={this.handleDocumentLoadSuccess}
            >
              <Page
                pageNumber={currentPage}
                scale={scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>

            {imageSrc && (
              <div
                ref={this.imageRef}
                style={{
                  position: 'absolute',
                  top: imageY,
                  left: imageX,
                  width: imageWidth,
                  height: imageHeight,
                  border: '1px dashed #000',
                  cursor: 'move',
                  userSelect: 'none',
                }}
                onMouseDown={this.handleDragStart}
              >
                <img
                  src={imageSrc}
                  alt="Підпис"
                  style={{ width: '100%', height: '100%', pointerEvents: 'none' }}
                  draggable={false}
                />
                <div
                  onMouseDown={this.handleResizeStart}
                  style={{
                    position: 'absolute',
                    right: 0,
                    bottom: 0,
                    width: 10,
                    height: 10,
                    background: 'blue',
                    cursor: 'nwse-resize',
                  }}
                />
              </div>
            )}
          </div>

          <div style={{
            marginTop: 7,
            marginBottom: 7,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 10,
          }}>
            <button className="btn btn-default" onClick={this.generateResizedImage}>
              Зберегти позицію та зображення
            </button>
            <button
              className="btn btn-default"
              onClick={() => this.setState((prev) => ({ currentPage: Math.max(1, prev.currentPage - 1) }))}
              disabled={currentPage <= 1}
            >
              Назад
            </button>
            <span>Сторінка {currentPage} з {numPages}</span>
            <button
              className="btn btn-default"
              onClick={() => this.setState((prev) => ({ currentPage: Math.min(numPages, prev.currentPage + 1) }))}
              disabled={currentPage >= numPages}
            >
              Вперед
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default PDFPreviewer;